import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface CreateAssignmentProps {
  domain: string;
}

interface Assignment {
  id: string;
  assignmentId: string;
  title: string;
  description: string;
  class: string;
  section: string;
  subject: string;
  teacherId: string;
  teacherName: string;
  assignedDate: string;
  dueDate: string;
  totalMarks: number;
  status: string;
  createdAt: string;
}

const CreateAssignment: React.FC<CreateAssignmentProps> = ({ domain }) => {
  const [activeView, setActiveView] = useState<'list' | 'create'>('list');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    class: '',
    section: '',
    subject: '',
    teacherId: '',
    teacherName: '',
    assignedDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    totalMarks: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchAssignments();
  }, [domain]);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      // Use school-data path for assignments
      const q = query(
        collection(db, `school-data/${domain}/assignments`),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      const assignmentList: Assignment[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Assignment));
      setAssignments(assignmentList);
    } catch (error: any) {
      console.error('Error fetching assignments:', error);
      alert(`Failed to load assignments: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(false);

    try {
      const assignmentData = {
        ...formData,
        assignmentId: `assign_${Date.now()}`,
        totalMarks: Number(formData.totalMarks),
        status: 'active',
        attachments: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await addDoc(collection(db, `school-data/${domain}/assignments`), assignmentData);

      setSuccess(true);
      // Reset form
      setFormData({
        title: '',
        description: '',
        class: '',
        section: '',
        subject: '',
        teacherId: '',
        teacherName: '',
        assignedDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        totalMarks: '',
      });

      // Refresh list
      setTimeout(() => {
        fetchAssignments();
        setActiveView('list');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating assignment:', error);
      alert(`Failed to create assignment: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteDoc(doc(db, `school-data/${domain}/assignments`, assignmentId));
      alert('Assignment deleted successfully!');
      fetchAssignments();
    } catch (error: any) {
      console.error('Error deleting assignment:', error);
      alert(`Failed to delete assignment: ${error.message}`);
    }
  };

  const handleStatusChange = async (assignmentId: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, `school-data/${domain}/assignments`, assignmentId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      alert('Status updated successfully!');
      fetchAssignments();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  };

  const getFilteredAssignments = () => {
    return assignments.filter(assignment => {
      if (filterClass && assignment.class !== filterClass) return false;
      if (filterSection && assignment.section !== filterSection) return false;
      if (filterStatus !== 'all' && assignment.status !== filterStatus) return false;
      return true;
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: string } = {
      active: 'bg-success',
      completed: 'bg-secondary',
      cancelled: 'bg-danger',
    };
    return badges[status] || 'bg-secondary';
  };

  const filteredAssignments = getFilteredAssignments();

  if (activeView === 'create') {
    return (
      <div className="card shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-file-earmark-plus me-2 text-primary-custom"></i>
              Create Assignment
            </h5>
            <small className="text-muted">Assign tasks and projects to students</small>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setActiveView('list')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to List
          </button>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label">Assignment Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Description *</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Class *</label>
                <select
                  className="form-select"
                  value={formData.class}
                  onChange={(e) => handleInputChange('class', e.target.value)}
                  required
                >
                  <option value="">Select Class</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Class {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Section *</label>
                <select
                  className="form-select"
                  value={formData.section}
                  onChange={(e) => handleInputChange('section', e.target.value)}
                  required
                >
                  <option value="">Select Section</option>
                  {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                    <option key={sec} value={sec}>
                      Section {sec}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Subject *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="e.g., Mathematics"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Teacher ID *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.teacherId}
                  onChange={(e) => handleInputChange('teacherId', e.target.value)}
                  placeholder="e.g., 17PVSS0001"
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Teacher Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.teacherName}
                  onChange={(e) => handleInputChange('teacherName', e.target.value)}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Assigned Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.assignedDate}
                  onChange={(e) => handleInputChange('assignedDate', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Due Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  required
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Total Marks *</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.totalMarks}
                  onChange={(e) => handleInputChange('totalMarks', e.target.value)}
                  min="1"
                  required
                />
              </div>
            </div>

            {success && (
              <div className="alert alert-success mt-4">
                <i className="bi bi-check-circle me-2"></i>
                Assignment created successfully!
              </div>
            )}

            <div className="mt-4">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2"></i>
                    Create Assignment
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi bi-file-earmark-text me-2 text-primary-custom"></i>
              Assignments
            </h5>
            <small className="text-muted">Manage and track all assignments</small>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setActiveView('create')}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Create New Assignment
          </button>
        </div>

        {/* Filters */}
        <div className="row g-2 mt-3">
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
            >
              <option value="">All Classes</option>
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  Class {i + 1}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={filterSection}
              onChange={(e) => setFilterSection(e.target.value)}
            >
              <option value="">All Sections</option>
              {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                <option key={sec} value={sec}>
                  Section {sec}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select form-select-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="col-md-3 text-end">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setFilterClass('');
                setFilterSection('');
                setFilterStatus('all');
              }}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading assignments...</p>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox display-1 text-muted"></i>
            <p className="mt-3 text-muted">
              {assignments.length === 0
                ? 'No assignments created yet. Click "Create New Assignment" to get started.'
                : 'No assignments match the selected filters.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Class/Section</th>
                  <th>Subject</th>
                  <th>Teacher</th>
                  <th>Assigned Date</th>
                  <th>Due Date</th>
                  <th>Marks</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td>
                      <strong>{assignment.title}</strong>
                      <br />
                      <small className="text-muted">{assignment.description.substring(0, 50)}...</small>
                    </td>
                    <td>
                      <span className="badge bg-info text-dark">
                        {assignment.class} - {assignment.section}
                      </span>
                    </td>
                    <td>{assignment.subject}</td>
                    <td>
                      {assignment.teacherName}
                      <br />
                      <small className="text-muted">{assignment.teacherId}</small>
                    </td>
                    <td>{assignment.assignedDate}</td>
                    <td>{assignment.dueDate}</td>
                    <td>{assignment.totalMarks}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(assignment.status)}`}>
                        {assignment.status}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          title="Change Status"
                          onClick={() => {
                            const newStatus = assignment.status === 'active' ? 'completed' : 'active';
                            handleStatusChange(assignment.id, newStatus);
                          }}
                        >
                          <i className="bi bi-arrow-repeat"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete"
                          onClick={() => handleDelete(assignment.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Summary Stats */}
        {!loading && filteredAssignments.length > 0 && (
          <div className="row mt-4">
            <div className="col-md-3">
              <div className="card bg-light">
                <div className="card-body text-center">
                  <h3 className="mb-0">{filteredAssignments.length}</h3>
                  <small className="text-muted">Total Assignments</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-success bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-success">
                    {filteredAssignments.filter(a => a.status === 'active').length}
                  </h3>
                  <small className="text-muted">Active</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-secondary bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-secondary">
                    {filteredAssignments.filter(a => a.status === 'completed').length}
                  </h3>
                  <small className="text-muted">Completed</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card bg-danger bg-opacity-10">
                <div className="card-body text-center">
                  <h3 className="mb-0 text-danger">
                    {filteredAssignments.filter(a => a.status === 'cancelled').length}
                  </h3>
                  <small className="text-muted">Cancelled</small>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAssignment;
